import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBullhorn } from '@fortawesome/free-solid-svg-icons';
import { announcementService } from '../services/announcementService';

const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const AnnouncementBanner = ({ targetAudience = 'user', ctaLabel: propCtaLabel, ctaHref: propCtaHref }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  // Hide for current session (immediate dismiss)
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(new Set());
  // Last seen timestamps persisted to localStorage
  const [seenMap, setSeenMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
    // Load last-seen map; migrate legacy dismissed list
    try {
      const map = JSON.parse(localStorage.getItem('announcementSeenAt') || '{}');
      const legacy = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
      if (Array.isArray(legacy) && legacy.length) {
        const now = Date.now();
        legacy.forEach((id) => { map[id] = now; });
        localStorage.removeItem('dismissedAnnouncements');
        localStorage.setItem('announcementSeenAt', JSON.stringify(map));
      }
      setSeenMap(map);
    } catch {}
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService.getActive();
      // Filter announcements based on target audience
      const filteredAnnouncements = data.filter(announcement => 
        announcement.targetAudience === 'all' || announcement.targetAudience === targetAudience
      );
      setAnnouncements(filteredAnnouncements);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAnnouncement = (announcementId) => {
    const id = announcementId;
    const newDismissed = new Set([...dismissedAnnouncements, id]);
    setDismissedAnnouncements(newDismissed);
    try {
      localStorage.setItem('dismissedAnnouncements', JSON.stringify([...newDismissed]));
      const updated = { ...(seenMap || {}) };
      const timestamp = Date.now();
      updated[id] = timestamp;
      setSeenMap(updated);
      localStorage.setItem('announcementSeenAt', JSON.stringify(updated));
    } catch {}
  };

  // Simplified styling (no type)
  const bannerClass = 'bg-gray-50 border border-gray-200 text-gray-800';

  const now = Date.now();
  const visibleAnnouncements = announcements.filter((announcement) => {
    const id = announcement._id || announcement.id;
    if (dismissedAnnouncements.has(id)) return false;
    const lastSeen = seenMap[id];
    return !lastSeen || (now - lastSeen) >= COOLDOWN_MS;
  });

  // Mark currently visible announcements as seen (for cooldown) without hiding them now
  useEffect(() => {
    if (!visibleAnnouncements.length) return;
    try {
      const updated = { ...(seenMap || {}) };
      const timestamp = Date.now();
      visibleAnnouncements.forEach((a) => {
        const id = a._id || a.id;
        updated[id] = timestamp;
      });
      setSeenMap(updated);
      localStorage.setItem('announcementSeenAt', JSON.stringify(updated));
    } catch {}
  }, [visibleAnnouncements]);

  if (loading) {
    return null; // Don't show loading state for announcements
  }

  const getDefaultCta = () => {
    switch (targetAudience) {
      case 'admin': return { label: 'Manage Announcements', href: '/dashboard/admin' };
      case 'agent': return { label: 'View Shipments', href: '/dashboard/agent/shipments' };
      case 'ecommerce': return { label: 'View Plans', href: '/auth/plan-selection' };
      case 'user':
      case 'all':
      default:
        return { label: 'View Plans', href: '/auth/plan-selection' };
    }
  };

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {visibleAnnouncements.map((announcement) => (
        <div
          key={announcement.id}
          className={`rounded-lg p-4 ${bannerClass} relative`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0 text-gray-600 mr-3 mt-0.5">
              <FontAwesomeIcon icon={faBullhorn} className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {announcement.imageData && (
                    <img src={announcement.imageData} alt="announcement" className="w-full max-h-48 object-cover rounded mb-2" />
                  )}
                  <p className="text-sm opacity-90">{announcement.message}</p>
                  {/* CTA Button */}
                  <div className="mt-3">
                    {(() => {
                      const cta = { label: propCtaLabel, href: propCtaHref };
                      const fallback = getDefaultCta();
                      const label = cta.label || announcement.ctaLabel || fallback.label;
                      const href = cta.href || announcement.ctaUrl || fallback.href;
                      return (
                        <button
                          onClick={() => {
                            try { navigate(href); } catch { window.location.href = href; }
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                        >
                          {label}
                        </button>
                      );
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => dismissAnnouncement(announcement.id)}
                  className="flex-shrink-0 ml-4 p-1 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors"
                  title="Dismiss announcement"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm opacity-60" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
